import React, { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate, useParams } from "react-router-dom";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { useSearchParams } from "react-router-dom";
import { getTemplateVersions } from "../utils/Services";
import TemplateVersion from "./TemplateVersion";
import { ITemplate, ITemplateVersion } from "../utils/Types";
import {
  FileText,
  Stethoscope,
  Heartbeat,
  Thermometer,
  Brain,
  Users,
  Syringe,
  FirstAidKit,
  Pill,
} from "@phosphor-icons/react";
import TemplateVersionDetail from "./TemplateVersionDetail";
import { useIsAdmin } from "@/hooks/useIsAdmin";

interface TemplateVersionsProps {
  modalOpen?: boolean;
  closeModal?: () => void;
}

const TemplateVersions: React.FC<TemplateVersionsProps> = ({
  modalOpen,
  closeModal,
}) => {
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const [templatesCount, setTemplatesCount] = useState(0);
  const [templates, setTemplates] = useState<ITemplateVersion[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedVersion = searchParams.get("version");
  const { height, width } = useWindowDimensions();
  const isAdmin = useIsAdmin();

  const { templateId = "" } = useParams<{
    templateId: string;
  }>();

  const options = {
    history_present_illness: {
      text: "History of Present Illness",
      icon: <FileText size={20} />,
    },
    review_of_systems: {
      text: "Review of Systems",
      icon: <Thermometer size={20} />,
    },
    physical_exam: {
      text: "Physical Exam",
      icon: <Stethoscope size={20} />,
    },
    differential_diagnosis: {
      text: "Differential Diagnosis",
      icon: <Heartbeat size={20} />,
    },
    medical_decision_making: {
      text: "Medical Decision Making",
      icon: <Brain size={20} />,
    },
    consults: {
      text: "Consults",
      icon: <Users size={20} />,
    },
    procedures: {
      text: "Procedures",
      icon: <Syringe size={20} />,
    },
    critical_care_note: {
      text: "Critical Care Note",
      icon: <FirstAidKit size={20} />,
    },
    disposition: {
      text: "Disposition",
      icon: <Pill size={20} />,
    },
  };

  const handleTemplatePublished = (response: ITemplate) => {
    if (response?.published) {
      setTemplates((prevTemplates) => {
        return prevTemplates.map((template) => {
          if (template.id === response?.published_version?.id) {
            return { ...template, published: true, draft: false };
          }

          return { ...template, published: false };
        });
      });
    }
  };

  const fetchTemplates = async (off = 0) => {
    setIsTemplatesLoading(true);

    try {
      const response = await getTemplateVersions(templateId, {
        limit: limit,
        offset: off,
        ...(isAdmin && { admin_dashboard: isAdmin }),
      });

      setTemplates((prev) => [...prev, ...response?.results]);

      setTemplatesCount(response?.count);
    } catch (error) {
    } finally {
      setIsTemplatesLoading(false);
    }
  };

  const fetchMoreTemplates = () => {
    try {
      setOffset(offset + limit);
      fetchTemplates(offset + limit);
    } catch (error) {}
  };

  useEffect(() => {
    if (modalOpen) {
      setOffset(0);
      setTemplates([]);
      const res = setTimeout(async () => {
        fetchTemplates(0);
      }, 500);

      return () => {
        clearTimeout(res);
      };
    }
  }, [modalOpen]);

  return (
    <div className="flex flex-col md:flex-row">
      <div className={`${width < 768 && selectedVersion ? "hidden" : "block"}`}>
        <div
          id="scrollableDivTemplates"
          className="dark:text-white md:w-[350px] w-full md:h-[calc(100dvh-160px)] h-[95vh] overflow-auto"
        >
          <InfiniteScroll
            dataLength={templates.length}
            next={fetchMoreTemplates}
            hasMore={templatesCount > templates.length}
            loader={
              <div className="w-full text-center p-2">
                <span className="loader w-5 h-5"></span>
              </div>
            }
            scrollableTarget="scrollableDivTemplates"
          >
            <ul role="list" className="space-y-3 mx-2">
              {templates.map((template) => (
                <TemplateVersion
                  key={`${template?.id}-${template?.version}`}
                  template={template}
                  // @ts-ignore
                  icon={options[template?.category!]?.icon}
                  // @ts-ignore
                  category={options[template?.category!]?.text}
                />
              ))}
            </ul>
          </InfiniteScroll>
        </div>
      </div>

      <div className="hidden md:flex w-[1px] bg-gray-50 dark:bg-gray-700 mx-2 items-center justify-center" />

      {templates.length > 0 && (
        <div
          className={`${
            width < 768 && !selectedVersion ? "hidden" : "block"
          } flex-1`}
        >
          <TemplateVersionDetail
            templates={templates}
            modalOpen={modalOpen!}
            closeModal={closeModal!}
            onTemplatePublished={handleTemplatePublished}
            key={JSON.stringify(templates.map((t) => t.published))}
          />
        </div>
      )}
    </div>
  );
};

export default TemplateVersions;
